import React, { useState, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  StatusBar,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
} from "react-native";
import { FontFamilies } from "@/constants/Typography";
import { ActionButton } from "@/components/ActionButton";
import { router, Stack } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import useFinanceDataStore from "@/store/financeData";
import {
  parseDisplayToCents,
  handleCurrencyInputChange,
  formatCentsToDisplay,
} from "@/utils/myMoney";
import { showError } from "@/utils/toast";

// Budget categories with emoji and display name
const BUDGET_CATEGORIES = [
  { key: "ENTERTAINMENT", emoji: "🎬", label: "Entertainment" },
  { key: "FOOD_AND_DRINK", emoji: "🍽️", label: "Food & Drink" },
  { key: "GENERAL_MERCHANDISE", emoji: "🛍️", label: "General Merchandise" },
  { key: "PERSONAL_CARE", emoji: "💅", label: "Personal Care" },
  { key: "TRAVEL", emoji: "✈️", label: "Travel" },
  { key: "GENERAL_SERVICES", emoji: "🛠️", label: "General Services" },
  { key: "HOME_IMPROVEMENT", emoji: "🏡", label: "Home improvement" },
  { key: "MEDICAL", emoji: "🏥", label: "Medical" },
  { key: "TRANSPORTATION", emoji: "🚗", label: "Transportation" },
] as const;

type CategoryKey = (typeof BUDGET_CATEGORIES)[number]["key"];

const SetCategoryBudgetScreen = () => {
  const { updateCategoryBudgets } = useFinanceDataStore();
  const [categoryBudgets, setCategoryBudgets] = useState<Record<CategoryKey, string>>(
    () => {
      const initial: Record<string, string> = {};
      BUDGET_CATEGORIES.forEach((cat) => {
        initial[cat.key] = "";
      });
      return initial as Record<CategoryKey, string>;
    }
  );
  const [focusedCategory, setFocusedCategory] = useState<CategoryKey | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const inputRefs = useRef<Record<string, TextInput | null>>({});

  const handleBack = () => {
    router.back();
  };

  const handleCategoryChange = (category: CategoryKey, text: string) => {
    setCategoryBudgets((prev) => ({
      ...prev,
      [category]: handleCurrencyInputChange(text),
    }));
  };

  // Calculate total budget from all categories
  const totalBudgetCents = BUDGET_CATEGORIES.reduce((sum, cat) => {
    return sum + parseDisplayToCents(categoryBudgets[cat.key]);
  }, 0);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Build categories array with non-zero budgets
      const categories = BUDGET_CATEGORIES.map((cat) => ({
        category: cat.key,
        budgetAmount: parseDisplayToCents(categoryBudgets[cat.key]),
      })).filter((cat) => cat.budgetAmount >= 0);

      console.log("[SetCategoryBudget] Saving categories:", JSON.stringify(categories));
      const success = await updateCategoryBudgets(categories);
      console.log("[SetCategoryBudget] updateCategoryBudgets returned:", success);

      if (success) {
        router.push("/signup/budget-success");
      } else {
        showError("Failed to save budget");
      }
    } catch (error) {
      console.error("[SetCategoryBudget] Error:", error);
      showError("Failed to save budget");
    } finally {
      setIsSaving(false);
    }
  };

  const renderCategoryRow = (category: (typeof BUDGET_CATEGORIES)[number]) => {
    const value = categoryBudgets[category.key];
    const isFocused = focusedCategory === category.key;
    const hasValue = parseDisplayToCents(value) > 0;

    return (
      <View key={category.key} style={styles.categoryRow}>
        <View style={styles.categoryLabelContainer}>
          <Text style={styles.categoryLabel}>
            {category.emoji} {category.label}
          </Text>
        </View>
        <View
          style={[
            styles.inputBox,
            isFocused && styles.inputBoxFocused,
          ]}
        >
          <TextInput
            ref={(ref) => (inputRefs.current[category.key] = ref)}
            style={[
              styles.categoryInput,
              !hasValue && !isFocused && styles.categoryInputPlaceholder,
            ]}
            value={value}
            onChangeText={(text) => handleCategoryChange(category.key, text)}
            keyboardType="decimal-pad"
            placeholder="$0.00"
            placeholderTextColor="#A3A3A3"
            onFocus={() => setFocusedCategory(category.key)}
            onBlur={() => setFocusedCategory(null)}
            selection={value ? { start: value.length, end: value.length } : undefined}
          />
        </View>
      </View>
    );
  };

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false, gestureEnabled: true }} />
      <StatusBar barStyle="dark-content" />
      <SafeAreaView style={styles.safeArea}>
        {/* Back Button */}
        <View style={styles.headerContainer}>
          <TouchableOpacity style={styles.backButton} onPress={handleBack}>
            <Ionicons name="chevron-back" size={28} color="#161D14" />
          </TouchableOpacity>
        </View>

        {/* Content */}
        <View style={styles.content}>
          {/* Header */}
          <View style={styles.headerSection}>
            <Text style={styles.title}>Set your budget by category</Text>
            <Text style={styles.subtitle}>
              Distribute your budget across categories like Food, Shopping, Bills, and more. You can edit or add categories anytime.
            </Text>
          </View>

          {/* Categories Card */}
          <View style={styles.categoriesCard}>
            <ScrollView
              style={styles.categoriesScroll}
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              <View style={styles.categoriesContainer}>
                {BUDGET_CATEGORIES.map(renderCategoryRow)}
              </View>

              {/* Total Section */}
              <View style={styles.totalSection}>
                <View style={styles.totalRow}>
                  <Text style={styles.totalLabel}>Total monthly budget</Text>
                  <Text style={styles.totalAmount}>
                    {formatCentsToDisplay(totalBudgetCents)}
                  </Text>
                </View>
              </View>
            </ScrollView>
          </View>
        </View>

        {/* Bottom Section */}
        <View style={styles.bottomSection}>
          <ActionButton
            title={isSaving ? "" : "Save"}
            onPress={handleSave}
            disabled={isSaving}
          >
            {isSaving && <ActivityIndicator color="#FFFFFF" />}
          </ActionButton>
        </View>
      </SafeAreaView>
    </View>
  );
};

export default SetCategoryBudgetScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#FFFFFF",
  },
  safeArea: {
    flex: 1,
  },
  headerContainer: {
    paddingHorizontal: 16,
    paddingTop: 8,
  },
  backButton: {
    width: 28,
    height: 28,
    justifyContent: "center",
    alignItems: "center",
  },
  content: {
    flex: 1,
    paddingHorizontal: 16,
    paddingTop: 16,
  },
  headerSection: {
    gap: 8,
    marginBottom: 24,
  },
  title: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 30,
    lineHeight: 37,
    color: "#161D14",
  },
  subtitle: {
    fontFamily: FontFamilies.Inter.Regular,
    fontSize: 15,
    lineHeight: 23,
    color: "#161D14",
    opacity: 0.7,
  },
  categoriesCard: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    shadowColor: "#6F6F6F",
    shadowOffset: { width: -3, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 17,
    elevation: 4,
  },
  categoriesScroll: {
    flex: 1,
  },
  categoriesContainer: {
    padding: 16,
    paddingTop: 20,
    gap: 20,
  },
  categoryRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  categoryLabelContainer: {
    flex: 1,
  },
  categoryLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 15,
    lineHeight: 23,
    color: "#000000",
  },
  inputBox: {
    width: 80,
    height: 36,
    borderWidth: 1,
    borderColor: "#EBEBEB",
    borderRadius: 4,
    paddingHorizontal: 12,
    justifyContent: "center",
    alignItems: "center",
  },
  inputBoxFocused: {
    borderColor: "#0F9129",
  },
  categoryInput: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 17,
    height: 36,
    textAlignVertical: "center",
    color: "#0E3115",
    textAlign: "right",
    padding: 0,
  },
  categoryInputPlaceholder: {
    color: "#A3A3A3",
  },
  totalSection: {
    borderTopWidth: 1,
    borderTopColor: "#EBEBEB",
    paddingTop: 12,
    paddingHorizontal: 16,
    paddingBottom: 8,
    marginTop: 4,
  },
  totalRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  totalLabel: {
    fontFamily: FontFamilies.Inter.Medium,
    fontSize: 13,
    lineHeight: 23,
    color: "#A3A3A3",
  },
  totalAmount: {
    fontFamily: FontFamilies.Inter.SemiBold,
    fontSize: 17,
    lineHeight: 27,
    color: "#0E3115",
  },
  bottomSection: {
    paddingHorizontal: 16,
    paddingBottom: 16,
  },
});
